import {
	GameCategory,
	getAchievementMetadata,
	getProbabilityTypesForCategory,
	PROBABILITY_ACHIEVEMENT_TYPES,
	type UserAchievementsType,
} from '@sportspredict/shared';
import { ACHIEVEMENT_COPY } from '@/constants';
import type { AchievementDisplayItem } from '../types/probability-event-web-home.types';

export function shouldShowProfileAchievements(categoryId?: string | null): boolean {
	return categoryId !== GameCategory.NFL && categoryId !== GameCategory.EPL;
}

interface ProbabilityAchievementLike {
	type: UserAchievementsType;
	deleted_date?: string | null;
	created_date?: string | null;
}

export function getAchievementMeta(type: UserAchievementsType, categoryId?: string | null) {
	return {
		...(ACHIEVEMENT_COPY[type] as { title: string; icon?: string } | undefined),
		...getAchievementMetadata(type, categoryId),
	} as
		| {
				title: string;
				icon?: string;
		  }
		| undefined;
}

export function getAchievementDisplayItems(
	achievements: ProbabilityAchievementLike[],
	options: { maxItems?: number | null; minItems?: number; categoryId?: string | null } = {}
): AchievementDisplayItem[] {
	const { maxItems: maxItemsOpt, minItems: minItemsOpt, categoryId } = options;
	const maxItems = maxItemsOpt ?? 3;
	const minItems = minItemsOpt ?? (maxItems === null ? 3 : maxItems);
	const earnedProbabilityAchievements = achievements
		.filter((achievement) => {
			return !achievement.deleted_date && PROBABILITY_ACHIEVEMENT_TYPES.includes(achievement.type);
		})
		.sort((a, b) => {
			if (!a.created_date) return 1;
			if (!b.created_date) return -1;
			return new Date(b.created_date).getTime() - new Date(a.created_date).getTime();
		})
		.slice(0, maxItems === null ? undefined : maxItems)
		.map((achievement) => ({ type: achievement.type, isEarned: true }));

	if (earnedProbabilityAchievements.length >= minItems) {
		return earnedProbabilityAchievements;
	}

	const earnedTypes = new Set(earnedProbabilityAchievements.map((achievement) => achievement.type));
	const fallbackPool = categoryId
		? getProbabilityTypesForCategory(categoryId)
		: (PROBABILITY_ACHIEVEMENT_TYPES as UserAchievementsType[]);
	const fallback = fallbackPool
		.filter((type) => !earnedTypes.has(type))
		.slice(0, minItems - earnedProbabilityAchievements.length)
		.map((type) => ({ type, isEarned: false }));

	return [...earnedProbabilityAchievements, ...fallback];
}
